/**
 * マークダウン形式のドキュメントを構造化データにパースするユーティリティ。
 *
 * バックエンドがマークダウン形式でドキュメントを生成する場合でも
 * PageView の構造化表示に利用できるよう、パース処理をここに集約する。
 */

interface Table {
  headers: string[];
  rows: string[][];
}

interface SystemOverviewData {
  概要: string;
  背景: string[];
  その他: string[];
  メリット?: string[];
  導入するポイント?: string[];
  特徴?: string[];
  主な機能: Table;
  業務フロー: string[];
  目的: string[];
  ユースケーステーブル: Table;
}

export interface FunctionalRequirementRowData {
  fid: number | string;
  業務: string;
  機能: string;
  利用ユーザー: string;
  実装方法: string;
  備考: string;
}

export const normalizeMarkdownContent = (content: string): string => {
  let normalized = content;
  if (normalized.startsWith('"') && normalized.endsWith('"')) {
    try {
      normalized = JSON.parse(normalized);
    } catch {
      // パースできない場合はそのまま使用
    }
  }
  return normalized.replace(/\\n/g, "\n").replace(/\\"/g, '"').replace(/\\t/g, "\t");
};

// ── 内部ヘルパー ────────────────────────────────────────────────────────────

/**
 * マークダウンテーブルの1行を文字列配列にパースする。
 * `| aaa | bbb | ccc |` → `["aaa", "bbb", "ccc"]`
 */
const parseTableRow = (line: string): string[] =>
  line
    .split("|")
    .map((cell) => cell.trim())
    .filter((_, index, arr) => index !== 0 && index !== arr.length - 1);

/** セパレータ行（`|---|---|`）かどうかを判定する */
const isSeparatorRow = (line: string): boolean => /^\|[\s|:-]+\|$/.test(line);

/**
 * マークダウンテーブルブロックを `Table` 型にパースする。
 * テーブルが見つからない・不正な場合は空テーブルを返す。
 */
const parseMarkdownTable = (lines: string[]): Table => {
  const tableLines = lines.filter((l) => l.trimStart().startsWith("|"));
  if (tableLines.length < 2) {
    return { headers: [], rows: [] };
  }

  const headers = parseTableRow(tableLines[0]!);
  const dataLines = tableLines.slice(1).filter((l) => !isSeparatorRow(l));
  const rows = dataLines.map(parseTableRow);

  return { headers, rows };
};

/**
 * リスト形式の行（`- xxx` / `1. xxx`）からテキスト部分を抽出する。
 */
const parseListItems = (lines: string[]): string[] =>
  lines
    .map((l) => l.trim())
    .filter((l) => l.startsWith("- ") || /^\d+\.\s/.test(l))
    .map((l) =>
      l
        .replace(/^-\s+/, "")
        .replace(/^\d+\.\s+/, "")
        .trim(),
    )
    .filter(Boolean);

// ── 公開 API ────────────────────────────────────────────────────────────────

/**
 * システム概要書のマークダウンを `SystemOverviewData` 互換の構造にパースする。
 *
 * 期待するマークダウン形式:
 * ```
 * # 提案システム名
 * ## システム概要
 * ### 目的
 * - xxx
 * ### 主な機能
 * | 機能 | 概要 |
 * | xxx | yyy |
 * ...
 * ```
 *
 * パースに失敗したセクションは空配列 / 空テーブルにフォールバックする。
 * マークダウン全体が空や無効な場合は `null` を返す。
 */
export const parseSystemOverviewMarkdown = (
  markdown: string,
): { systemName: string; data: SystemOverviewData } | null => {
  if (!markdown || !markdown.trim()) {
    return null;
  }

  const normalized = normalizeMarkdownContent(markdown);

  const lines = normalized.split("\n");

  // `# ...` 行からシステム名を抽出
  const systemNameLine = lines.find((l) => l.startsWith("# "));
  const systemName = systemNameLine ? systemNameLine.replace(/^#\s+/, "").trim() : "提案システム";

  // `## システム概要` と最初の `### ` の間のテキストを概要として取得
  const overviewLines: string[] = [];
  let inOverviewSection = false;

  for (const line of lines) {
    if (line.startsWith("## ")) {
      inOverviewSection = line.includes("システム概要");
      continue;
    }
    if (inOverviewSection && line.startsWith("### ")) {
      inOverviewSection = false;
    }
    if (inOverviewSection && line.trim() !== "") {
      overviewLines.push(line.trim());
    }
  }

  const 概要 = overviewLines.join("\n");

  // `### セクション名` で各セクションを分割
  const sections: Record<string, string[]> = {};
  let currentSection: string | null = null;

  for (const line of lines) {
    if (line.startsWith("### ")) {
      currentSection = line.replace(/^###\s+/, "").trim();
      sections[currentSection] = [];
    } else if (currentSection !== null) {
      // `##` 以上の見出しはセクション区切りとして扱う（`### ` 以外）
      if (line.startsWith("## ") || (line.startsWith("# ") && !line.startsWith("### "))) {
        currentSection = null;
      } else {
        sections[currentSection]!.push(line);
      }
    }
  }

  const getListSection = (name: string): string[] => {
    const sectionLines = sections[name];
    if (!sectionLines || sectionLines.length === 0) return [];
    return parseListItems(sectionLines);
  };

  const getTableSection = (name: string): Table => {
    const sectionLines = sections[name];
    if (!sectionLines || sectionLines.length === 0) return { headers: [], rows: [] };
    return parseMarkdownTable(sectionLines);
  };

  const tokuchouItems = getListSection("特徴");
  const meritItems = getListSection("メリット");
  const pointItems = getListSection("導入するポイント");
  // 優先順: 特徴 > 導入するポイント > メリット
  const resolvedItems =
    tokuchouItems.length > 0 ? tokuchouItems : pointItems.length > 0 ? pointItems : meritItems;

  const data: SystemOverviewData = {
    概要,
    背景: getListSection("背景"),
    目的: getListSection("目的"),
    主な機能: getTableSection("主な機能"),
    ...(resolvedItems.length > 0 ? { 特徴: resolvedItems } : {}),
    その他: getListSection("その他"),
    業務フロー: getListSection("業務フロー"),
    ユースケーステーブル: getTableSection("ユースケーステーブル"),
  };

  // 何も抽出できなかった場合は null を返して呼び出し元にフォールバックさせる
  const hasAnyContent =
    data.概要 !== "" ||
    data.背景.length > 0 ||
    data.目的.length > 0 ||
    data.主な機能.headers.length > 0 ||
    (data.特徴?.length ?? 0) > 0 ||
    data.その他.length > 0 ||
    data.業務フロー.length > 0 ||
    data.ユースケーステーブル.headers.length > 0;

  if (!hasAnyContent) {
    return null;
  }

  return { systemName, data };
};

/**
 * 機能要件書のマークダウンテーブルを `RowData[]` にパースする。
 *
 * 期待するマークダウン形式:
 * ```
 * | 要求機能 | 必要機能 | 誰が | どこで | 何をする | 実装方法 | 備考 |
 * |---|---|---|---|---|---|---|
 * | xxx | yyy | ... |
 * ```
 *
 * テーブルが見つからない / ヘッダーが取得できない場合は空配列を返す。
 */
export const parseFunctionalRequirementsMarkdown = (
  markdown: string,
): FunctionalRequirementRowData[] => {
  if (!markdown || !markdown.trim()) {
    return [];
  }

  const normalized = normalizeMarkdownContent(markdown);

  const lines = normalized.split("\n").map((l) => l.trim());
  const tableLines = lines.filter((l) => l.startsWith("|"));

  if (tableLines.length < 2) {
    return [];
  }

  const headers = parseTableRow(tableLines[0]!);
  if (headers.length === 0) {
    return [];
  }

  // ヘッダー名 → 列インデックスのマップを構築
  const colIndex: Record<string, number> = {};
  for (let i = 0; i < headers.length; i++) {
    colIndex[headers[i]!] = i;
  }

  // 保存データのカラム名で機能要件テーブルかどうか判定
  const SOURCE_COLUMNS = ["要求機能", "必要機能", "誰が", "実装方法"] as const;
  const hasRequiredColumn = SOURCE_COLUMNS.some((col) => col in colIndex);
  if (!hasRequiredColumn) {
    return [];
  }

  const getCellValue = (row: string[], col: string): string => {
    const idx = colIndex[col];
    return idx !== undefined ? (row[idx] ?? "") : "";
  };

  const dataLines = tableLines.slice(1).filter((l) => !isSeparatorRow(l));

  // 保存データのカラム名から表示用カラム名へマッピング
  // 要求機能→業務, 必要機能→機能, 誰が→利用ユーザー, 実装方法→実装方法, 何をする→備考
  return dataLines
    .map((line, rowIndex) => {
      const cells = parseTableRow(line);
      return {
        fid: rowIndex,
        業務: getCellValue(cells, "要求機能"),
        機能: getCellValue(cells, "必要機能"),
        利用ユーザー: getCellValue(cells, "誰が"),
        実装方法: getCellValue(cells, "実装方法"),
        備考: getCellValue(cells, "何をする"),
      } satisfies FunctionalRequirementRowData;
    })
    .filter(
      // 全フィールドが空の行は除外
      (row) => row.業務 || row.機能 || row.利用ユーザー || row.実装方法,
    );
};
